// Prospect invites — a rep invites a new customer with their order attached
// (squish-backend lib/prospect-invite.js; the Dashboard's + Prospect Cart
// button, the cart builder's send step and the invites board on the Sales tab).
//
// The rules a later edit could quietly get wrong live here, where they are
// run by test/prospect-invites.test.js:
//   - which link a rep copies. An invite's link finishes an ACCOUNT; a cart
//     link only shows the cart. Copying the cart link for an invite would send
//     the customer to a sign-in they cannot complete.
//   - what the rep is told after sending. "Sent" must never be said about an
//     email that did not go out.
//   - what each card offers. A paid or cancelled cart has nothing to resend.

// Status → how a card reads. `tone` is a CSS class on the card (App.css .inv-*).
export const INVITE_STATUS = {
  sent:             { label: 'Invite sent',      tone: 'is-waiting', open: true },
  expired:          { label: 'Link expired',     tone: 'is-expired', open: true },
  'account-set-up': { label: 'Account set up',   tone: 'is-active',  open: true },
  'cart-sent':      { label: 'Cart link sent',   tone: 'is-quiet',   open: true },
  paid:             { label: 'Paid',             tone: 'is-done',    open: false },
  cancelled:        { label: 'Cancelled',        tone: 'is-quiet',   open: false },
};

export function statusMeta(status) {
  return INVITE_STATUS[status] || { label: status ? String(status) : 'Unknown', tone: 'is-quiet', open: false };
}

// What a card can do. Before the account is set up a rep resends the INVITE
// (and can copy a fresh invite link); after, the cart link.
export function inviteActions(status) {
  if (status === 'sent' || status === 'expired') return { resend: 'Resend invite', copy: 'Copy invite link' };
  if (status === 'account-set-up' || status === 'cart-sent') return { resend: 'Resend cart link', copy: 'Copy cart link' };
  return { resend: null, copy: null };
}

// Whether a card offers Cancel cart. Only open work: a paid cart is refunded,
// not cancelled (squish-backend refuses it), and a cancelled one is done.
export function canCancel(status) {
  return statusMeta(status).open;
}

// What the board shows: everything still in play plus paid history. A
// cancelled cart is gone from the board, which is what the rep asked for when
// they cancelled it.
export function boardInvites(invites) {
  return sortInvites((Array.isArray(invites) ? invites : []).filter(i => i.status !== 'cancelled'));
}

// The link to hand a rep from a send or resend result.
export function linkFor(result) {
  if (!result) return null;
  if (result.mode === 'invite') return result.inviteUrl || null;
  return result.cartUrl || null;
}

// Open work first (still waiting on the customer), newest first within each.
export function sortInvites(invites) {
  const list = Array.isArray(invites) ? [...invites] : [];
  return list.sort((a, b) => {
    const ao = statusMeta(a.status).open ? 0 : 1;
    const bo = statusMeta(b.status).open ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return (Number(b.lastSentAt || b.sentAt) || 0) - (Number(a.lastSentAt || a.sentAt) || 0);
  });
}

// What the rep reads right after sending (or resending). Honest about email
// delivery: when the email did not go out, the headline says so and the link
// is the way forward.
export function sendOutcome(result, email) {
  const who = email || 'the customer';
  if (!result) return null;
  const emailed = result.emailed === true;
  if (result.mode === 'invite') {
    return emailed
      ? { ok: true, headline: `Invite sent to ${who}`, detail: 'They choose a password from the link and land on checkout, signed in. The link works once, for 7 days.' }
      : { ok: false, headline: `Account created, but the email to ${who} did not go out`, detail: `${result.emailError || 'The email could not be sent.'} Copy the invite link and send it yourself.` };
  }
  if (result.mode === 'existing-account') {
    return emailed
      ? { ok: true, headline: `${who} already has a Squish account`, detail: 'No new account was made. They were sent the cart link and sign in the usual way.' }
      : { ok: false, headline: `${who} already has a Squish account, and the cart email did not go out`, detail: `${result.emailError || 'The email could not be sent.'} Copy the cart link and send it yourself.` };
  }
  if (result.mode === 'account-set-up') {
    return emailed
      ? { ok: true, headline: `Cart link sent to ${who}`, detail: 'Their account is already set up, so they sign in from the link.' }
      : { ok: false, headline: `The cart email to ${who} did not go out`, detail: result.emailError || 'Copy the cart link and send it yourself.' };
  }
  return { ok: emailed, headline: emailed ? `Sent to ${who}` : 'Not sent', detail: result.emailError || '' };
}

// "$134.87 · 2 items" for a card's meta line.
export function cartSummary(invite) {
  const total = Number(invite?.total) || 0;
  const n = Number(invite?.itemCount) || 0;
  const money = `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${money} · ${n} ${n === 1 ? 'item' : 'items'}`;
}
